import styled from 'styled-components';
import { FC, useMemo, useRef } from 'react';
import { usePopup } from 'components/Popup';
import { Link } from 'react-router-dom';
import { Button } from '../Button';
import { Cell } from 'ui/Grid';

type DocumentCardProps = {
    title: string;
    url: string;
    link?: string;
    previewUrl?: string;
};

const DocumentImage = styled.img`
    width: 100%;
    height: auto;
    cursor: pointer;
`;

const CellContent = styled.div`
    width: 100%;
    max-width: 500px;
    margin: auto;
`;

const PopupContent = styled.div`
    padding: 35px 35px 13px;
`;

const DownloadButton = styled(Button)`
    margin: 10px auto 0;
`;

function imgToBase64(
    imgElement: HTMLImageElement,
    callback: (base64String: string) => void,
    outputFormat?: string
) {
    const canvas = document.createElement('canvas');
    canvas.width = imgElement.naturalWidth;
    canvas.height = imgElement.naturalHeight;

    const ctx = canvas.getContext('2d');
    ctx?.drawImage(imgElement, 0, 0);

    const base64String = canvas.toDataURL(outputFormat || 'image/png');
    callback(base64String);
    canvas.remove();
}

const prepareFileName = (title: string): string => {
    return title
        .replace(/[<>:"/\\|?*]/g, '_') // Заменяем недопустимые символы на подчеркивание
        .replace(/\s+/g, '_') // Заменяем пробелы на подчеркивание
        .trim();
};

export const DocumentCard: FC<DocumentCardProps> = ({
    title,
    url,
    previewUrl,
    link
}) => {
    const imageRef = useRef<HTMLImageElement>(null);

    const popupContent = useMemo(() => {
        const downloadImage = () =>
            new Promise<void>((resolve, reject) => {
                if (imageRef.current) {
                    try {
                        imgToBase64(imageRef.current, (base64String) => {
                            const a = document.createElement('a');
                            a.href = base64String;
                            a.download = prepareFileName(title) + '.png';
                            a.target = '_blank';
                            a.click();
                            a.remove();
                            resolve();
                        });
                    } catch (error) {
                        reject(error);
                    }
                }
            });

        return (
            <PopupContent>
                <DocumentImage
                    ref={imageRef}
                    src={previewUrl ?? url}
                    alt={title}
                />
                <DownloadButton onClick={downloadImage}>Скачать</DownloadButton>
            </PopupContent>
        );
    }, [previewUrl, url, title]);

    const openPopup = usePopup(popupContent);

    const content = <DocumentImage src={previewUrl ?? url} alt={title} />;

    const finalContent = link ? (
        <Link to={link}>{content}</Link>
    ) : (
        <div onClick={openPopup}>{content}</div>
    );

    return (
        <Cell width={4}>
            <CellContent>{finalContent}</CellContent>
        </Cell>
    );
};
